/**
 * Named filesystem-mode presets (G-B) — thin factories that compose existing primitives into a
 * `Partial<AgentOptions>`, so a consumer can pick a posture without hand-wiring the fs/tools:
 *
 *   new Agent(sandboxAgentOptions({ ai }))        // in-memory VFS — real disk untouched
 *   new Agent(diskAgentOptions({ ai }))           // jailed real disk @ cwd (the default)
 *   new Agent(await fullAgentOptions({ ai }))     // disk + a real /bin/sh tool (run binaries)
 *
 * The core stays unopinionated (IoC): these just preset `fs`/`tools`. `sandbox`/`disk` are edge-safe to
 * import; `full` is node-only (real shell) and DYNAMIC-imports it, so importing this module never pulls
 * `node:child_process` into an edge/browser bundle.
 */
import { MemFilesystem } from '@livx.cc/wcli/core';
import type { AgentOptions } from './Agent';
import { defaultTools, type AgentTool } from './tools';

/** Sandbox mode: an in-memory VFS — the real disk is never read or written. Edge/browser/test/dry-run. */
export function sandboxAgentOptions(opts: Partial<AgentOptions> = {}): Partial<AgentOptions> {
  return { fs: new MemFilesystem(), ...opts };
}

/** Disk mode (the default): operate on the real filesystem, jailed to cwd (secrets hidden by DEFAULT_DENY).
 *  Omitting `fs` lets the Agent lazily resolve the jailed disk — this preset just makes the intent explicit
 *  (and is where you'd thread overrides). Pass an explicit `fs` to use a different backend. */
export function diskAgentOptions(opts: Partial<AgentOptions> = {}): Partial<AgentOptions> {
  return { ...opts }; // fs omitted → Agent.ensureFs() resolves Jailed(NodeDisk(cwd))
}

/** Full mode: disk + a real `/bin/sh` tool (run installed binaries — git, bun, node, …) plus its
 *  background-job companions. NODE-ONLY: the real-shell module is dynamic-imported so this file stays
 *  edge-safe to import. `cwd` defaults to `process.cwd()`; secret env is scrubbed from spawned children. */
export async function fullAgentOptions(
  opts: Partial<AgentOptions> & {
    cwd?: string;
    /**
     * Push a finished background job back to the MODEL (Claude-Code parity). Without it `background:true`
     * is write-only: the job finishes and nothing ever tells the model — so the Shell tool's description
     * drops its cross-turn promise too, rather than lying to the model about this host.
     *
     * Wire it to the agent you are about to build — the closure runs long after construction:
     *   const agent = new Agent(await fullAgentOptions({ ai, onJobExit: (n) => agent.inject(n) }));
     */
    onJobExit?: (notice: string) => void;
    /**
     * Bring your OWN registry instead of letting the preset build one. This is the handle a preset host
     * otherwise never gets (the preset returns `AgentOptions`, not the registry), which is what left
     * `ShellJobRegistry.setOnExit()` unreachable from this path — a host can now wire (or rewire) the
     * completion callback after the Agent exists:
     *   const jobs = new ShellJobRegistry({ cwd, killOnExit: true });
     *   const agent = new Agent(await fullAgentOptions({ ai, jobs }));
     *   jobs.setOnExit((n) => agent.inject(formatJobExit(n)));
     * `onJobExit` still works and, when both are given, is applied to the registry you passed.
     */
    jobs?: import('./tools.shell').ShellJobRegistry;
  } = {},
): Promise<Partial<AgentOptions>> {
  const { makeRealShellTool, makeShellJobTools, ShellJobRegistry, formatJobExit } = await import('./tools.shell');
  const { cwd = process.cwd(), tools, onJobExit, jobs, ...rest } = opts;
  const registry = jobs ?? new ShellJobRegistry({ cwd, killOnExit: true });
  if (onJobExit) registry.setOnExit((n) => onJobExit(formatJobExit(n)));
  const shell: AgentTool[] = [makeRealShellTool({ cwd, registry }), ...makeShellJobTools(registry)];
  return { ...rest, tools: [...(tools ?? defaultTools()), ...shell] };
}
